import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  CheckCircle,
  CreditCard,
  Eye,
  EyeOff,
  Key,
  Loader2,
  Save,
  ToggleLeft,
  ToggleRight,
  Webhook,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { usePlatformStripeConfig, useUpdatePlatformStripeConfig } from '@/hooks/usePlatformConfig';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { PageLayout } from '@/components/ui/PageLayout';
import { ErrorAlert } from '@/components/ui/ErrorAlert';

export function AdminStripeSettings() {
  const { t } = useTranslation();
  const { data: config, isLoading, error, refetch } = usePlatformStripeConfig();
  const updateConfig = useUpdatePlatformStripeConfig();

  const [testSecretKey, setTestSecretKey] = useState('');
  const [liveSecretKey, setLiveSecretKey] = useState('');
  const [testWebhookSecret, setTestWebhookSecret] = useState('');
  const [liveWebhookSecret, setLiveWebhookSecret] = useState('');
  const [showTestKey, setShowTestKey] = useState(false);
  const [showLiveKey, setShowLiveKey] = useState(false);
  const [showTestWebhook, setShowTestWebhook] = useState(false);
  const [showLiveWebhook, setShowLiveWebhook] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-ds-muted" />
      </div>
    );
  }

  if (error) {
    return <ErrorAlert message={t('admin.stripe.load_error')} />;
  }

  const isTestMode = config?.stripe_mode === 'test';
  const isLiveMode = config?.stripe_mode === 'live';

  async function handleToggleMode() {
    const newMode = isTestMode ? 'live' : 'test';

    if (newMode === 'live' && !config?.has_live_secret_key) {
      toast.error(t('admin.stripe.live_key_required'));
      return;
    }

    try {
      await updateConfig.mutateAsync({ stripe_mode: newMode });
      toast.success(t('admin.stripe.mode_changed', { mode: newMode }));
    } catch (err) {
      console.error('Failed to toggle mode', err);
      toast.error(t('admin.stripe.mode_change_error'));
    }
  }

  async function handleSaveTestKeys(e: FormEvent) {
    e.preventDefault();
    if (!testSecretKey && !testWebhookSecret) {
      toast.error(t('admin.stripe.no_changes'));
      return;
    }

    try {
      await updateConfig.mutateAsync({
        stripe_test_secret_key: testSecretKey || undefined,
        stripe_test_webhook_secret: testWebhookSecret || undefined,
      });
      toast.success(t('admin.stripe.test_keys_saved'));
      setTestSecretKey('');
      setTestWebhookSecret('');
      refetch();
    } catch (err) {
      console.error('Failed to save test keys', err);
      toast.error(t('admin.stripe.save_error'));
    }
  }

  async function handleSaveLiveKeys(e: FormEvent) {
    e.preventDefault();
    if (!liveSecretKey && !liveWebhookSecret) {
      toast.error(t('admin.stripe.no_changes'));
      return;
    }

    try {
      await updateConfig.mutateAsync({
        stripe_live_secret_key: liveSecretKey || undefined,
        stripe_live_webhook_secret: liveWebhookSecret || undefined,
      });
      toast.success(t('admin.stripe.live_keys_saved'));
      setLiveSecretKey('');
      setLiveWebhookSecret('');
      refetch();
    } catch (err) {
      console.error('Failed to save live keys', err);
      toast.error(t('admin.stripe.save_error'));
    }
  }

  const webhookUrl = 'https://pupgbtuzlamtircfdamp.supabase.co/functions/v1/stripe-webhook';

  return (
    <PageLayout
      title={t('admin.stripe.title')}
      subtitle={t('admin.stripe.subtitle')}
      contentClassName="flex flex-col gap-s5"
    >

      {/* Mode Toggle Card */}
      <div className="rounded-lg border border-ds-line bg-ds-card p-6 shadow-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div
              className={`flex h-12 w-12 items-center justify-center rounded-full ${
                isLiveMode ? 'bg-ds-green-tint' : 'bg-ds-amber-tint'
              }`}
            >
              <CreditCard className={`h-6 w-6 ${isLiveMode ? 'text-ds-green' : 'text-ds-amber'}`} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-ds-text">
                {t('admin.stripe.current_mode')}
              </h2>
              <p className="text-sm text-ds-muted">
                {isLiveMode ? t('admin.stripe.mode_live_desc') : t('admin.stripe.mode_test_desc')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span
              className={`rounded-full px-3 py-1 text-sm font-medium ${
                isLiveMode
                  ? 'bg-ds-green-tint text-ds-green-ink'
                  : 'bg-ds-amber-tint text-ds-amber-ink'
              }`}
            >
              {isLiveMode ? t('admin.stripe.live') : t('admin.stripe.test')}
            </span>
            <Button
              type="button"
              variant="secondary"
              onClick={handleToggleMode}
              disabled={updateConfig.isPending}
            >
              {updateConfig.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : isTestMode ? (
                <ToggleLeft className="h-4 w-4" />
              ) : (
                <ToggleRight className="h-4 w-4" />
              )}
              <span className="ml-2">{t('admin.stripe.switch_mode')}</span>
            </Button>
          </div>
        </div>

        {isLiveMode && (
          <div className="mt-4 flex items-start gap-3 rounded-md border border-ds-red-line bg-ds-red-tint p-4">
            <AlertTriangle className="h-5 w-5 flex-shrink-0 text-ds-red" />
            <div className="text-sm text-ds-red-ink">
              <p className="font-medium">{t('admin.stripe.live_warning_title')}</p>
              <p className="mt-1">{t('admin.stripe.live_warning_desc')}</p>
            </div>
          </div>
        )}
      </div>

      {/* Webhook URL Info */}
      <div className="rounded-lg border border-ds-teal-line bg-ds-teal-tint p-4">
        <div className="flex items-start gap-3">
          <Webhook className="h-5 w-5 flex-shrink-0 text-ds-teal" />
          <div className="flex-1">
            <h3 className="font-medium text-ds-text">{t('admin.stripe.webhook_url_title')}</h3>
            <p className="mt-1 text-sm text-ds-muted">{t('admin.stripe.webhook_url_desc')}</p>
            <div className="mt-2 flex items-center gap-2">
              <code className="flex-1 rounded bg-ds-card px-2 py-1 text-xs text-ds-text">
                {webhookUrl}
              </code>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => {
                  navigator.clipboard.writeText(webhookUrl);
                  toast.success(t('admin.stripe.copied'));
                }}
              >
                {t('admin.stripe.copy')}
              </Button>
            </div>
            <p className="mt-2 text-xs text-ds-teal">
              {t('admin.stripe.webhook_events')}: account.updated, checkout.session.completed,
              payment_intent.payment_failed, payment_intent.canceled
            </p>
          </div>
        </div>
      </div>

      {/* Test Keys Section */}
      <div className="rounded-lg border border-ds-line bg-ds-card p-6 shadow-card">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-ds-amber-tint">
            <Key className="h-5 w-5 text-ds-amber" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-ds-text">
              {t('admin.stripe.test_keys_title')}
            </h2>
            <p className="text-sm text-ds-muted">{t('admin.stripe.test_keys_desc')}</p>
          </div>
        </div>

        <form onSubmit={handleSaveTestKeys} className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ds-text">
              {t('admin.stripe.secret_key')}
            </label>
            <Input
              type={showTestKey ? 'text' : 'password'}
              value={testSecretKey}
              onChange={(e) => setTestSecretKey(e.target.value)}
              placeholder={
                config?.has_test_secret_key
                  ? (config.stripe_test_secret_key_masked ?? 'sk_test_****')
                  : 'sk_test_...'
              }
              trailingIcon={
                <button
                  type="button"
                  onClick={() => setShowTestKey(!showTestKey)}
                  className="hover:text-ds-text"
                >
                  {showTestKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              }
            />
            {config?.has_test_secret_key && (
              <p className="mt-1 flex items-center gap-1 text-xs text-ds-green">
                <CheckCircle className="h-3 w-3" />
                {t('admin.stripe.key_configured')}
              </p>
            )}
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-ds-text">
              {t('admin.stripe.webhook_secret')}
            </label>
            <Input
              type={showTestWebhook ? 'text' : 'password'}
              value={testWebhookSecret}
              onChange={(e) => setTestWebhookSecret(e.target.value)}
              placeholder={
                config?.has_test_webhook_secret
                  ? (config.stripe_test_webhook_secret_masked ?? 'whsec_****')
                  : 'whsec_...'
              }
              trailingIcon={
                <button
                  type="button"
                  onClick={() => setShowTestWebhook(!showTestWebhook)}
                  className="hover:text-ds-text"
                >
                  {showTestWebhook ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              }
            />
            {config?.has_test_webhook_secret && (
              <p className="mt-1 flex items-center gap-1 text-xs text-ds-green">
                <CheckCircle className="h-3 w-3" />
                {t('admin.stripe.key_configured')}
              </p>
            )}
          </div>

          <Button
            type="submit"
            disabled={updateConfig.isPending || (!testSecretKey && !testWebhookSecret)}
          >
            {updateConfig.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {t('admin.stripe.save_test_keys')}
          </Button>
        </form>
      </div>

      {/* Live Keys Section */}
      <div className="rounded-lg border border-ds-line bg-ds-card p-6 shadow-card">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-ds-green-tint">
            <Key className="h-5 w-5 text-ds-green" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-ds-text">
              {t('admin.stripe.live_keys_title')}
            </h2>
            <p className="text-sm text-ds-muted">{t('admin.stripe.live_keys_desc')}</p>
          </div>
        </div>

        <form onSubmit={handleSaveLiveKeys} className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ds-text">
              {t('admin.stripe.secret_key')}
            </label>
            <Input
              type={showLiveKey ? 'text' : 'password'}
              value={liveSecretKey}
              onChange={(e) => setLiveSecretKey(e.target.value)}
              placeholder={
                config?.has_live_secret_key
                  ? (config.stripe_live_secret_key_masked ?? 'sk_live_****')
                  : 'sk_live_...'
              }
              trailingIcon={
                <button
                  type="button"
                  onClick={() => setShowLiveKey(!showLiveKey)}
                  className="hover:text-ds-text"
                >
                  {showLiveKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              }
            />
            {config?.has_live_secret_key && (
              <p className="mt-1 flex items-center gap-1 text-xs text-ds-green">
                <CheckCircle className="h-3 w-3" />
                {t('admin.stripe.key_configured')}
              </p>
            )}
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-ds-text">
              {t('admin.stripe.webhook_secret')}
            </label>
            <Input
              type={showLiveWebhook ? 'text' : 'password'}
              value={liveWebhookSecret}
              onChange={(e) => setLiveWebhookSecret(e.target.value)}
              placeholder={
                config?.has_live_webhook_secret
                  ? (config.stripe_live_webhook_secret_masked ?? 'whsec_****')
                  : 'whsec_...'
              }
              trailingIcon={
                <button
                  type="button"
                  onClick={() => setShowLiveWebhook(!showLiveWebhook)}
                  className="hover:text-ds-text"
                >
                  {showLiveWebhook ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              }
            />
            {config?.has_live_webhook_secret && (
              <p className="mt-1 flex items-center gap-1 text-xs text-ds-green">
                <CheckCircle className="h-3 w-3" />
                {t('admin.stripe.key_configured')}
              </p>
            )}
          </div>

          <Button
            type="submit"
            disabled={updateConfig.isPending || (!liveSecretKey && !liveWebhookSecret)}
          >
            {updateConfig.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {t('admin.stripe.save_live_keys')}
          </Button>
        </form>
      </div>

      {/* Last Updated */}
      {config?.updated_at && (
        <p className="text-center text-xs text-ds-muted">
          {t('admin.stripe.last_updated')}: {new Date(config.updated_at).toLocaleString()}
        </p>
      )}
    </PageLayout>
  );
}
